<template>
  <b-card no-body>
    <b-card-body class="">
      <div class="d-lg-flex justify-content-between align-items-center w-100 mb-10px mb-lg-0">
        <div>
          <h2 class="font-weight-bold font-medium-3 mb-10px mb-lg-0">
            {{ t('My Consignment Documents') }}
          </h2>
        </div>
        <div class="d-flex align-items-center">
          <b-dropdown variant="primary" size="md" class="p-5-10px" :text="t('Create Document')">
            <b-dropdown-item
              v-for="item in dropdownItems"
              :key="item.permission"
              @click="createDocument(item.type)"
            >
              {{ item.label }}
            </b-dropdown-item>
          </b-dropdown>
          <b-button
            variant="primary"
            type="button"
            size="md"
            class="p-12-12px add-new-document-btn"
            @click="addNewDocument"
          >
            <feather-icon icon="PlusIcon" size="16" class="mr-50" />
            {{ t('Add New Document') }}
          </b-button>
        </div>
      </div>

      <div v-if="documents.length" class="mt-2">
        <table class="w-100 table table-bordered-layout table-sm">
          <thead>
            <tr>
              <th>#</th>
              <th>
                {{ t('Document Type') }}
              </th>
              <th>
                {{ t('File Type') }}
              </th>
              <th>
                {{ t('Creation Date') }}
              </th>
              <th>
                {{ t('Document') }}
              </th>
            </tr>
          </thead>
          <tbody class="font-small-3">
            <tr :key="index + 'document'" v-for="(document, index) in documents">
              <td class="font-weight-bolder">
                {{ index + 1 }}
              </td>
              <td>
                {{ getDocumentTypeName(document.type) }}
              </td>
              <td>
                <feather-icon
                  :icon="getDocumentFileType(document) === 'pdf' ? 'FileTextIcon' : 'ImageIcon'"
                  size="16"
                />
                <span> .{{ getDocumentFileType(document) }} </span>
              </td>
              <td>
                {{ document.created_at }}
              </td>
              <td class="consignment-download-button-field">
                <a
                  href="javascript:void(0)"
                  class="cursor-pointer"
                  :class="[
                    downloadDocumentSubmitting &&
                      downloadDocumentId === document.id &&
                      'pointer-events-none'
                  ]"
                  @click="downloadDocument(document)"
                >
                  {{
                    downloadDocumentSubmitting && downloadDocumentId === document.id
                      ? t('Please Wait')
                      : t('Download Document')
                  }}
                </a>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else>
        {{ t('No documents found') }}
      </div>

      <b-modal
        id="consignment-documents-modal"
        ref="consignment-documents-modal"
        cancel-variant="outline-secondary"
        :ok-title="t('Save')"
        :cancel-title="t('Close')"
        centered
        :title="t('Add New Document')"
        size="sm"
        :ok-disabled="submitting"
        @hidden="resetModal"
        @ok="handleOk"
      >
        <b-overlay :show="submitting" rounded="md" variant="transparent">
          <validation-observer ref="consignmentNewDocumentForm">
            <b-form>
              <b-row>
                <b-col sm="12">
                  <b-form-group>
                    <div class="d-flex form-group-label">
                      <label for="name">{{ t('Document Type') }}</label>
                      <span class="text-danger">*</span>
                    </div>
                    <validation-provider
                      #default="{ errors }"
                      :name="t('Document Type')"
                      rules="required"
                    >
                      <b-form-select
                        id="type"
                        v-model="type"
                        name="type"
                        :class="[errors[0] && 'is-invalid']"
                        :options="consignmentDocumentTypes"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>

                  <b-form-group>
                    <div class="d-flex form-group-label">
                      <label for="name">{{ t('Document') }}</label>
                      <span class="text-danger">*</span>
                    </div>
                    <validation-provider
                      #default="{ errors }"
                      :name="t('Document')"
                      rules="required"
                    >
                      <b-form-file
                        v-model="files"
                        :placeholder="t('Choose a file or drop it here')"
                        :drop-placeholder="t('Drop file here')"
                        multiple
                        :file-name-formatter="formatNames"
                        :browse-text="t('Select File')"
                        accept=".jpg, .jpeg, .png, .pdf"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
              </b-row>
            </b-form>
          </validation-observer>
        </b-overlay>
      </b-modal>
    </b-card-body>
  </b-card>
</template>

<script>
import { useUtils as useI18nUtils } from '@core/libs/i18n';
import { helper } from '@core/mixins/helper.js';
import { ValidationProvider, ValidationObserver } from 'vee-validate';

import {
  BCard,
  BCardBody,
  BButton,
  BOverlay,
  BModal,
  BForm,
  BRow,
  BCol,
  BFormFile,
  BFormSelect,
  BFormGroup,
  BDropdown,
  BDropdownItem
} from 'bootstrap-vue';
import useService from '@/auth/service/useService';

export default {
  components: {
    BFormSelect,
    BCard,
    BCardBody,
    BButton,
    BOverlay,
    BModal,
    BForm,
    BRow,
    BCol,
    BFormFile,
    BFormGroup,
    BDropdown,
    BDropdownItem,

    ValidationProvider,
    ValidationObserver
  },
  mixins: [helper],
  props: {
    documents: {
      type: Array,
      default() {
        return [];
      }
    },
    consignmentId: {
      type: [Number, String],
      default: ''
    },
    permissions: {
      type: Object,
      default() {
        return {
          can_create_proforma_invoice: false,
          can_create_cn22: false,
          can_create_cn23: false
        };
      }
    }
  },
  data() {
    return {
      files: null,
      submitting: false,
      type: '',
      downloadDocumentSubmitting: false,
      downloadDocumentId: null,
      documentTypes: [],
      generatedDocument: null
    };
  },
  computed: {
    dropdownItems() {
      return [
        {
          label: this.$t('Create Proforma Invoice'),
          type: 'Proforma Invoice',
          permission: 'can_create_proforma_invoice'
        },
        {
          label: this.$t('Create CN22 Document'),
          type: 'CN22 Document',
          permission: 'can_create_cn22'
        },
        {
          label: this.$t('Create CN23 Document'),
          type: 'CN23 Document',
          permission: 'can_create_cn23'
        }
      ];
    },
    consignmentDocumentTypes() {
      const options = [{ text: this.$t('Make a Choice'), value: '' }];
      if (this.documentTypes && this.documentTypes.length > 0) {
        options.push(
          ...this.documentTypes.map(item => ({
            text: item.info,
            value: item.value
          }))
        );
      }
      return options;
    }
  },

  async created() {
    this.documentTypes = await this.getConsignmentDocumentType();
  },
  methods: {
    addNewDocument() {
      this.$refs['consignment-documents-modal'].show();
    },
    handleOk(bvModalEvent) {
      if (this.submitting) return;

      bvModalEvent.preventDefault();
      this.handleSubmit();
    },
    resetModal() {
      this.files = null;
    },
    handleSubmit() {
      this.$refs.consignmentNewDocumentForm.validate().then(success => {
        if (success) {
          const formData = new FormData();
          const consignmentId = this.$route.params.id;

          if (this.files) {
            const maxSizeInBytes = 20 * 1024 * 1024;
            let isFileTooLarge = false;

            this.files.forEach(file => {
              if (file.size > maxSizeInBytes) {
                isFileTooLarge = true;
              }
            });

            if (isFileTooLarge) {
              this.showErrorMessage(
                "Dosya boyutu çok büyük. Lütfen 20 MB'dan küçük bir dosya seçin."
              );
              return;
            }

            this.files.forEach(file => {
              formData.append('document_file', file);
              formData.append('document_type', this.type);
              formData.append('consignment_id', consignmentId);
            });
          }

          this.submitting = true;
          useService
            .uploadConsignmentDocument(formData)
            .then(response => {
              this.showSuccessMessage(
                `${response.message || this.$t('Upload Document Successfully')}`
              );

              this.files = null;
              this.type = '';

              this.$refs['consignment-documents-modal'].hide();
              this.$emit('reloadData', true);
            })
            .catch(messages => {
              if (typeof messages === 'object' && messages && messages.length) {
                messages.forEach(item => {
                  this.showErrorMessage(item.message);
                });
              }
            })
            .finally(() => {
              this.submitting = false;
            });
        }
      });
    },
    formatNames(files) {
      return files.length === 1 ? files[0].name : `${files.length} files selected`;
    },

    async createDocument(type) {
      let serviceMethod;
      switch (type) {
        case 'Proforma Invoice':
          serviceMethod = useService.createProformaInvoice.bind(useService);
          break;
        case 'CN22 Document':
          serviceMethod = useService.createCn22Document.bind(useService);
          break;
        case 'CN23 Document':
          serviceMethod = useService.createCn23Document.bind(useService);
          break;
        default:
          return;
      }

      try {
        const response = await serviceMethod(this.consignmentId);
        this.generatedDocument = response.data;
        this.$emit('reloadData', true);
        if (response.message) {
          this.showSuccessMessage(response.message);
        }
      } catch (error) {
        if (Array.isArray(error) && error.length > 0) {
          const errorMessage = error[0].message;
          this.showErrorMessage(errorMessage);
        } else {
          this.showErrorMessage(error);
        }
      }
    },

    getDocumentFileType(doc) {
      if (doc.file_type) return doc.file_type;
      if (doc.mime_type) {
        return doc.mime_type.split('/')[1];
      }
      return 'pdf';
    },

    downloadDocument(doc) {
      this.downloadDocumentSubmitting = true;
      this.downloadDocumentId = doc.id;

      const fileType = this.getDocumentFileType(doc);

      useService
        .downloadConsignmentDocument(doc.id)
        .then(response => {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `${Date.now()}.${fileType}`);
          document.body.appendChild(link);
          link.click();
        })
        .finally(() => {
          this.downloadDocumentSubmitting = false;
          this.downloadDocumentId = null;
        });
    }
  },
  setup() {
    const { t } = useI18nUtils();

    return {
      t
    };
  }
};
</script>

<style scoped>
.add-new-document-btn {
  display: flex;
  align-items: center;
  background-color: #dcac1f !important;
  border-color: #dcac1f !important;
}

.add-new-document-btn:hover {
  background-color: #e6b52a !important;
  border-color: #e6b52a !important;
}

.add-new-document-btn:focus,
.add-new-document-btn:active {
  background-color: #d4a423 !important;
  border-color: #d4a423 !important;
}
</style>
